import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal = ({ isOpen, onClose }: PrivacyPolicyModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display font-bold">Privacy Policy</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm font-body">
            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Information We Collect</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                When you use our triathlon checklist service, we collect and store the following information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Account information (email address, full name) when you register</li>
                <li>Triathlon checklist data, including sections, phases, and items you create</li>
                <li>Checklist completion status and notes you add</li>
                <li>Target race dates and countdown preferences</li>
                <li>Usage patterns and interaction data for service improvement</li>
                <li>Technical information (IP address, browser type, device information)</li>
                <li>Cookies and local storage for user preferences and authentication</li>
              </ul>
              <p className="text-foreground font-body leading-[1.7] mt-4">
                <strong>Session Data:</strong> If you create a checklist without signing up,
                we temporarily store your checklist data in your browser's session storage. 
                This data is only stored on your device and is automatically deleted when you 
                close your browser tab. It is not transmitted to our servers unless you choose 
                to sign up and save your checklist.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">How We Use Your Information</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Provide and maintain your triathlon checklist service account</li>
                <li>Store and sync your checklist data across devices</li>
                <li>Enable countdown timers and race preparation features</li>
                <li>Authenticate your account and maintain security</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve our service features and user experience</li>
                <li>Send important service-related notifications</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Third-Party Services</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                We use the following third-party services to operate our platform:
              </p>
               <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                 <li>Supabase (database hosting and user authentication)</li>
                 <li>Lovable.dev (hosting and deployment platform)</li>
                 <li>Google Analytics (website analytics and usage tracking)</li>
                 <li>Content Delivery Networks for performance optimization</li>
               </ul>
               <p className="text-muted-foreground leading-[1.65] mt-2">
                  Google Analytics collects anonymized data about website usage, including pages visited, 
                  time spent on site, geographic location (country/region), and device information. 
                  This helps us understand how our service is used and improve the user experience. 
                  You can opt out of analytics tracking through our cookie consent preferences.
                </p>
                <p className="text-muted-foreground leading-[1.65] mt-2">
                  These services have their own privacy policies and may collect information as described in their respective privacy policies.
                </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Data Storage and Security</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Your account data and checklists are stored securely in our cloud database hosted by Supabase. We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-2">
                <li>Row Level Security (RLS) policies ensuring you can only access your own data</li>
                <li>Encrypted data transmission (HTTPS/TLS)</li>
                <li>Secure authentication protocols</li>
                <li>Regular security monitoring and updates</li>
                <li>Data backup and recovery procedures</li>
              </ul>
              <p className="text-muted-foreground leading-[1.65]">
                Your data is stored in secure data centers with appropriate physical and digital security measures to protect against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Information Sharing</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                We do not sell, trade, or otherwise transfer your personal information to third 
                parties without your consent, except as described in this policy or as required by law.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Legal Basis for Processing (GDPR)</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                If you are located in the European Economic Area (EEA), UK, or Switzerland, we process your personal data based on the following legal grounds:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Contractual Necessity:</strong> Processing is necessary to provide our checklist service to you</li>
                <li><strong>Consent:</strong> You have given explicit consent for specific processing activities (e.g., marketing communications, analytics)</li>
                <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate business interests (e.g., fraud prevention, security, service improvement)</li>
                <li><strong>Legal Obligation:</strong> Processing is required to comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Your Rights Under GDPR and CCPA</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Depending on your location, you have the following rights regarding your personal information:
              </p>
              
              <h4 className="font-semibold text-foreground mt-3 mb-2">GDPR Rights (EEA, UK, Switzerland)</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Right of Access (Article 15):</strong> Request a copy of all personal data we hold about you</li>
                <li><strong>Right to Rectification (Article 16):</strong> Correct inaccurate or incomplete personal data</li>
                <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restriction (Article 18):</strong> Limit how we use your personal data</li>
                <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a structured, machine-readable format and transfer it to another service</li>
                <li><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interests or direct marketing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4 mb-2">CCPA Rights (California Residents)</h4>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                California residents have the following rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Right to Know:</strong> Request disclosure of personal information collected, used, disclosed, or sold in the past 12 months</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal information we have collected from you</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (Note: We do not sell personal information)</li>
                <li><strong>Right to Non-Discrimination:</strong> Not receive discriminatory treatment for exercising your privacy rights</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> Limit the use and disclosure of sensitive personal information</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4 mb-2">How to Exercise Your Rights</h4>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                To exercise any of these rights, please contact us at info@sinkbikerun.com or use our contact form. We will:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Respond to GDPR requests within 30 days (extendable to 60 days for complex requests)</li>
                <li>Respond to CCPA requests within 45 days (extendable to 90 days for complex requests)</li>
                <li>Verify your identity before processing requests to protect your privacy</li>
                <li>Provide the requested information free of charge (except for manifestly unfounded or excessive requests)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Data Retention Periods</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                We retain your personal data only as long as necessary for the purposes described in this Privacy Policy:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Account Data:</strong> Retained while your account is active and for 30 days after account deletion (to allow recovery)</li>
                <li><strong>Checklist Data:</strong> Retained while your account is active and permanently deleted 30 days after account deletion</li>
                <li><strong>Authentication Logs:</strong> Retained for 90 days for security and fraud prevention</li>
                <li><strong>Audit Logs:</strong> Retained for 1 year for security monitoring and compliance</li>
                <li><strong>Support Communications:</strong> Retained for 2 years for customer service quality and legal compliance</li>
                <li><strong>Analytics Data:</strong> Anonymized and aggregated data may be retained indefinitely for statistical purposes</li>
                <li><strong>Session Data (Non-Registered Users):</strong> Automatically deleted when browser session ends</li>
              </ul>
              <p className="text-muted-foreground leading-[1.65] mt-2">
                After the retention period expires, personal data is securely deleted or anonymized. You can request earlier deletion by contacting us or deleting your account.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">International Data Transfers</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Your personal data may be transferred to and processed in countries outside your country of residence, including the United States and other countries where our service providers operate.
              </p>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                <strong>For EEA, UK, and Switzerland users:</strong> When we transfer personal data outside these regions, we ensure adequate protection through:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                <li>Adequacy decisions recognizing equivalent data protection standards</li>
                <li>Additional safeguards such as encryption and access controls</li>
                <li>Service providers that comply with recognized privacy frameworks (e.g., Privacy Shield successor mechanisms)</li>
              </ul>
              <p className="text-muted-foreground leading-[1.65] mt-2">
                Our primary data infrastructure is provided by Supabase (US-based) with data centers that may be located globally. We have implemented appropriate technical and organizational measures to protect your data during international transfers.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Contact Us</h3>
              <p className="text-muted-foreground leading-[1.65]">
                If you have any questions about this Privacy Policy, please contact us through 
                our contact form or email us at info@sinkbikerun.com.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Changes to This Policy</h3>
              <p className="text-muted-foreground leading-[1.65]">
                We may update this Privacy Policy from time to time. We will notify you of any 
                changes by posting the new Privacy Policy on this page and updating the 
                "Last Updated" date.
              </p>
              <p className="text-foreground font-body leading-[1.7] mt-2">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyModal;